"use client";

import { planets } from "@ishvaram/domain-data/planets";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";

import { getCurrentTransits, type TransitPosition } from "@/lib/api/transits";
import { chromeLocaleForPath, type ChromeLocale } from "@/lib/chrome-locale";
// The light projection, not "@/lib/rashi": this strip ships on every page, and
// the barrel drags the compatibility engine and its tables in with it.
import { RASHI_MAP } from "@/lib/rashi-presentation";

/**
 * The sitewide "live gochar" strip.
 *
 * ## Why this fetches instead of rendering a constant
 *
 * This component previously shipped a module-level `LIVE_TRANSITS` array —
 * `{ graha: "Sun", sign: "Leo", degrees: "03°" }` and four more — as literal
 * source. It was labelled LIVE and rendered on every app page. Those are
 * astronomical facts, so rules 24 and 29 put them with the engine and forbid
 * them as literals anywhere else: the Moon changes rashi every ~2.3 days, so
 * the strip was publishing a wrong Moon sign most of the time, under a pulsing
 * "LIVE" dot, on a YMYL surface.
 *
 * The values now come from the engine at runtime. There is deliberately NO
 * fallback array: if the engine cannot be reached the planetary row renders
 * nothing at all. A stale-but-plausible position is the failure mode that got
 * us here, and an empty row is strictly better than a confident wrong one.
 *
 * ## Why the strip is app-runtime only
 *
 * Statically exported artifacts have their JavaScript stripped and are carried
 * forward between releases, so a value baked into one would be frozen for as
 * long as that artifact lives. `INTERACTIVE_ONLY_CHROME` in `@/lib/site-chrome`
 * records this as a declared difference between the two chrome projections
 * rather than leaving it as accidental drift. The quick links below are chrome
 * and DO appear on both.
 *
 * ## What it shows, and when (b-hc-05, c-a11y-14; 2026-10-07)
 *
 * Nothing until the engine answers: a bare pulsing "LIVE GOCHAR:" used to sit
 * in the sticky header for 5-11 s on every page, and forever when the engine
 * was slow. The row's height stays reserved either way (rule 58). Grahas and
 * rashis carry their Jyotish names (Surya, Kanya; सूर्य, कन्या on /hi/) rather
 * than Western ones and emoji, the pulse stops under prefers-reduced-motion,
 * and the scroller is a focusable, named region so a keyboard can scroll it.
 *
 * It stays inside the sticky header on phones for now: its 30px is part of
 * the 100px header that src/lib/sticky-chrome-offsets.ts (#2569) and every
 * result anchor are measured against, so letting it scroll away is a
 * follow-up that must move those offsets in the same change.
 */

/** Grahas the strip shows, in the order jyotish convention reads them. */
const RIBBON_GRAHAS = ["sun", "moon", "jupiter", "saturn", "rahu"] as const;

const GRAHA_NAMES = Object.fromEntries(
  planets.map((planet) => [planet.id, { en: planet.name_sa, hi: planet.name_hi }])
) as Readonly<Record<string, Record<ChromeLocale, string>>>;

const RIBBON_COPY: Record<ChromeLocale, { label: string; region: string; retrograde: string }> = {
  en: { label: "LIVE GOCHAR:", region: "Live planetary transits", retrograde: "(R)" },
  hi: { label: "आज का गोचर:", region: "ग्रहों का वर्तमान गोचर", retrograde: "(वक्री)" },
};

/**
 * `current_positions` is independent of the caller's moon rashi — the endpoint
 * needs one to score the personalised `active_transits` it also returns, which
 * this strip ignores. Mesh is the zero point of the zodiac and is used here as
 * that required-but-unused argument, never as a claim about the visitor.
 */
const POSITIONS_REFERENCE_RASHI = "mesh";

function rashiLabel(rashi: string, locale: ChromeLocale): string {
  const entry = RASHI_MAP[rashi];
  if (!entry) return rashi;
  return locale === "hi" ? entry.name_hi : entry.name_sa;
}

export default function EphemerisTransitRibbon() {
  const locale = chromeLocaleForPath(usePathname());
  const copy = RIBBON_COPY[locale];
  const [positions, setPositions] = useState<TransitPosition[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    getCurrentTransits(POSITIONS_REFERENCE_RASHI)
      .then((response) => {
        if (!cancelled) setPositions(response.current_positions);
      })
      .catch(() => {
        // Engine unreachable: show no positions rather than invented ones.
        if (!cancelled) setPositions([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const shown = (positions ?? [])
    .filter((p) => (RIBBON_GRAHAS as readonly string[]).includes(p.planet_id))
    .sort(
      (a, b) =>
        RIBBON_GRAHAS.indexOf(a.planet_id as (typeof RIBBON_GRAHAS)[number]) -
        RIBBON_GRAHAS.indexOf(b.planet_id as (typeof RIBBON_GRAHAS)[number])
    );
  const ready = shown.length > 0;

  return (
    <div
      className="border-b border-border-default bg-surface-raised py-1.5 px-3 text-caption overflow-x-auto scrollbar-none"
      data-design-owner="ephemeris-ribbon"
      {...(ready ? { role: "region", "aria-label": copy.region, tabIndex: 0 } : {})}
    >
      <div className="mx-auto flex max-w-6xl items-center justify-between gap-4">
        {/* Reserve the row height whether or not positions have arrived, so the
            strip never grows on hydration and pushes the page down (rule 58). */}
        <div className="flex min-h-[18px] items-center gap-4 shrink-0">
          {ready ? (
            <>
              <div className="flex items-center gap-1.5 font-bold text-intent-brand shrink-0">
                <span
                  aria-hidden="true"
                  className="inline-block w-1.5 h-1.5 rounded-full bg-intent-brand animate-ping motion-reduce:animate-none"
                />
                <span>{copy.label}</span>
              </div>

              <div className="flex items-center gap-3 text-text-secondary">
                {shown.map((position) => (
                  <span
                    key={position.planet_id}
                    className="inline-flex items-center gap-1 shrink-0 font-medium"
                  >
                    <span className="text-text-primary font-semibold">
                      {GRAHA_NAMES[position.planet_id]?.[locale] ?? position.planet_id}:
                    </span>
                    <span>
                      {rashiLabel(position.rashi, locale)} {Math.floor(position.rashi_degree)}&deg;
                    </span>
                    {position.is_retrograde && (
                      <span className="text-intent-brand font-bold text-caption">
                        {copy.retrograde}
                      </span>
                    )}
                  </span>
                ))}
              </div>
            </>
          ) : null}
        </div>

        {/* No quick links here: "Today's Panchang" duplicated the header nav's
            Panchang link (the header printed it twice), and /shani-transit/
            is listed on /daily-tools/. The header's one navigation tree owns
            the links (src/lib/site-chrome.ts). */}
      </div>
    </div>
  );
}
